import { AuthProvider } from "@/components/auth/AuthProvider"
import { ChunkErrorHandler } from "@/components/ChunkErrorHandler"
import { ProfileUpdateModalController } from "@/components/modals/ProfileUpdateModal"
import { QueryErrorBoundary } from "@/components/QueryErrorBoundary"
import { queryClient } from "@/config/queryClient"
import { ApiContext } from "@/context/ApiContext"
import { ProfileImageCacheProvider } from "@/context/ProfileImageCacheContext"
import {
  useLazyModules,
  usePreloadCriticalResources,
} from "@/hooks/useLazyModules"
import {
  useLoadBrandingImmediately,
  usePrefetchCriticalData,
} from "@/hooks/usePrefetch"
import store from "@/redux/store"
import { QueryClientProvider } from "@tanstack/react-query"
import axios from "axios"
import {
  lazy,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { BrowserRouter } from "react-router-dom"

// React Query Devtools solo se carga en desarrollo
const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools").then((m) => ({
    default: m.ReactQueryDevtools,
  }))
)

// ClickToComponent para abrir componentes en VS Code
const ClickToComponent = lazy(() =>
  import("click-to-react-component").then((m) => ({
    default: m.ClickToComponent,
  }))
)

// Componente interno que usa hooks de React Query
const AppContent: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
  // Cargar branding inmediatamente para estilos de la plataforma
  useLoadBrandingImmediately()

  // Prefetch datos críticos
  usePrefetchCriticalData()

  // Precargar recursos críticos
  usePreloadCriticalResources()

  // Cargar módulos de forma diferida (no bloquea la UI)
  useLazyModules(1500) // 1.5 segundos después de la carga inicial

  return (
    <>
      <TrackPageViews />
      {/*{fullstory?.active && <FullStory org={'TK1E0'} />}*/}
      <Layout show={!isLoading} />
      <Messages />
      <StreamedMessages />
      {/* Modal de actualización de datos del perfil — solo se muestra cuando
          is_active && required_update vienen true desde el backend */}
      {!isLoading && <ProfileUpdateModalController />}
    </>
  )
}

// TODO: remove this styles once migration to MUI its done
import "bootstrap/dist/css/bootstrap.min.css"
// import 'semantic-ui-css/semantic.min.css';

/** MUI Dependencies */
import { ThemeProvider } from "@mui/material/styles"
import getTheme from "./MUITheme"

/** Google Analytics */
import ReactGa from "react-ga4"

/** Services */

import {
  setLanguagesAction,
  setMeAction,
  setNotificationsCountAction,
  setServices, // Restaurado para servicios críticos
  setSurveyCount,
} from "@/redux/actions"

/** Utils */
import { objectEmpty } from "@/utils"
import { Settings } from "@/utils/luxon"

/** Components */
import { AlertContext } from "@/context/AlertContext"
import LoaderPage from "@/pages/Loader/LoaderPage"
import StreamedMessages from "@/ui2.0/Alerts/StreamedMessages"

import { TBranding } from "@/types"

import {
  getNotificationsCount,
  getOrgAvailableModules,
  getOrgSettings,
  getUser,
} from "@/service/_apiService"
import TrackPageViews from "@/TrackPageViews"
import { Suspense } from "react"
import APIDocs from "./pages/ApiDocs"
import { debugApiConfig, debugEnvironmentConfig } from "./utils/debugConfig"

// @ts-ignore
const Messages = lazy(() => import("@/ui/MessageContainer/MessagesContainer"))
const Layout = lazy(() => import("@/layouts"))
// const FullStory = lazy(() => import('react-fullstory'));

//Google Analytics 4
let ga_measure_id
if (/.*dev\.motivy.*/.test(window.origin)) {
  ga_measure_id = "G-7J9H5D94W8"
} else if (/.*pdb\.motivy.*/.test(window.origin)) {
  ga_measure_id = "G-0795SHCM2E"
} else if (/.*coderland\.motivy.*/.test(window.origin)) {
  ga_measure_id = "G-TZWKNZV8HD"
} else if (/.*olx\.motivy.*/.test(window.origin)) {
  ga_measure_id = "G-7LED68S89H"
} else if (/.*3acomposites\.motivy.*/.test(window.origin)) {
  ga_measure_id = "G-WD8CP2E4MN"
} else if (/.*demo\.motivy.*/.test(window.origin)) {
  ga_measure_id = "G-57745B3YW6"
} else if (/.*fiducia\.motivy.*/.test(window.origin)) {
  ga_measure_id = "G-SXZTWGDYK3"
} else if (/.*colourrepublic\.motivy.*/.test(window.origin)) {
  ga_measure_id = "G-3X5ECP49DD"
} else {
  ga_measure_id = "G-7J9H5D94W8"
}

ReactGa.initialize(ga_measure_id)
ReactGa.send({ hitType: "pageview", page: "/" })

const languages: Record<string, string> = {
  english: "en",
  spanish: "es",
}

/** Main component */
const App = (): ReactElement => {
  /** Hooks */
  const { i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [branding, setBranding] = useState<TBranding>(null)

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch("/version.json", { cache: "no-store" })
        const { version } = await res.json()
        const current = localStorage.getItem("app_version")
        if (current && current !== version) {
          window.location.reload() // fuerza reload ignorando cache
        }
        localStorage.setItem("app_version", version)
      } catch (err) {
        console.error("No se pudo verificar versión:", err)
      }
    }

    checkVersion()
  }, [])

  // Debug configuration on app start
  useEffect(() => {
    debugEnvironmentConfig()
    debugApiConfig()
  }, [])
  // const { fullstory } = useSelector((state: RootState) => state.services);

  /** Configs for login in with Teams */
  const [apiEndpoint] = useState(import.meta.env.VITE_APP_API_ENDPOINT || null)
  const [apiResponses, setApiResponses] = useState({
    token: localStorage.getItem("auth_token") || null,
    createdCommunicationGroup: null,
  })

  const setTeamsSession = useCallback(
    async (microsoftToken: string, referredId: string, subdomain: string) => {
      let response = null
      try {
        const r = await axios(`${apiEndpoint}/oauth2/teams/token`, {
          method: "POST",
          data: { token: microsoftToken, referredId: referredId },
          headers: { "X-Motivy-Origin": subdomain },
        })
        if (r && r.status === 200) {
          const t = r.data?.data?.auth_token
          setApiResponses((c) => ({
            ...c,
            token: t,
          }))
          response = t
          localStorage.setItem("auth_token", t)
        }
      } catch (error) {
        console.log(error)
        response = null
        // TODO Invoke sentry
      }
      return response
    },
    [apiEndpoint]
  )

  useEffect(() => {
    /** Modules available - ahora se maneja en useLazyModules */
    // async function getModulesLegacy() {
    // 	try {
    // 			const modules = await getOrgAvailableModules();
    // 			return modules || {};
    // 		} catch (error) {
    // 			console.warn("Error cargando módulos disponibles:", error);
    // 			// Retornar un objeto vacío en caso de error para que la app pueda continuar
    // 			return {};
    // 		}
    // }

    /** Notifications */
    const notifications = async () => {
      try {
        const { notification_count, pending_surveys } =
          await getNotificationsCount()
        store.dispatch(setNotificationsCountAction(notification_count || 0))
        store.dispatch(setSurveyCount(pending_surveys || 0))
      } catch (error) {
        console.warn("Error cargando notificaciones:", error)
        // Establecer valores por defecto en caso de error
        store.dispatch(setNotificationsCountAction(0))
        store.dispatch(setSurveyCount(0))
      }
    }

    /** Org settings */
    const orgSettings = async () => {
      try {
        const settings = await getOrgSettings()
        if (settings?.branding && !objectEmpty(settings.branding)) {
          setBranding(settings.branding)
        }
        // Guardar orgSettings en Redux para que esté disponible globalmente
        store.dispatch({
          type: "GET_ORG_SETTINGS",
          orgSettings: settings,
        })
      } catch (error) {
        console.warn("Error cargando configuraciones de organización:", error)
        // Continuar sin branding en caso de error
      }
    }

    /** Load user logged in */
    const loadUser = async () => {
      try {
        // Intenta obtener el usuario
        const user = await getUser()

        if (user) {
          // Si el usuario existe, gestionamos el idioma y notificaciones
          const userLang = languages[user.language] || "es"
          if (userLang !== i18n.language) {
            Settings.defaultLocale = userLang
            await i18n.changeLanguage(userLang)
          }

          // Solo cargar notificaciones si hay usuario autenticado
          await notifications()
        }

        // Carga de configuraciones de la organización, independientemente de si hay usuario o no
        await orgSettings()

        // Despachar las acciones de Redux
        store.dispatch(setLanguagesAction(Object.keys(languages)))
        store.dispatch(setMeAction(user ?? null, true))
      } catch (error) {
        console.error("Error cargando el usuario:", error)
        // Si ocurre algún error, podemos manejarlo o lanzar una acción de error
        store.dispatch(setMeAction(null, false)) // Marcar que no hay usuario
      }
    }

    /** Load critical services for newsfeed */
    const loadCriticalServices = async () => {
      try {
        const modules = await getOrgAvailableModules()
        if (modules) {
          // Priorizar servicios críticos para newsfeed con valores por defecto
          const criticalServices = {
            recognitions: modules.recognitions || { active: false },
            communications: modules.communications || { active: false },
            tasks: modules.tasks || { active: false },
            programs: modules.programs || { active: false },
            insignias: modules.insignias || { active: false },
            workplace: modules.workplace || { active: false },
            profile: modules.profile || { active: true }, // Activado para desarrollo
            notifications: modules.notifications || { active: false }, // Agregar servicio notifications
            goals: modules.goals || { active: false }, // Agregar servicio goals
            birthday_img: modules.birthday_img || { active: false }, // Agregar servicio birthday_img
            // Agregar otros servicios críticos según necesidad
            analytics: modules.analytics || { active: false },
            benefits: modules.benefits || { active: false },
            catalog: modules.catalog || { active: false },
            evaluations: modules.evaluations || { active: false },
            surveys: modules.surveys || { active: false },
            events: modules.events || { active: false },
            benef_corp: modules.benef_corp || { active: false },
            solicitud: modules.solicitud || { active: false },
            admin_retos: modules.admin_retos || { active: false },
            files_repo: modules.files_repo || { active: false },
            coverages: modules.coverages || { active: false },
            user_event_registration: modules.user_event_registration || {
              active: false,
            },
            ever_health: modules.ever_health || { active: false },
            salud_seguridad: modules.salud_seguridad || { active: false },
            invoices: modules.invoices || { active: false },
            publicBadges: modules.publicBadges || { active: false },
            maintenance: modules.maintenance || { active: false },
            // Panel de quejas (backend puede enviar complaints_pannel o complaints_panel)
            complaints_pannel:
              modules.complaints_pannel ||
              modules.complaints_panel ||
              { active: false },
            complaints: modules.complaints || { active: false },
            whatsapp: modules.whatsapp || { active: false },
            motivy_support: modules.motivy_support || { active: false },
            birthdays: modules.birthdays || { active: false },
            anniversary: modules.anniversary || { active: false },
            choose_catalog: modules.choose_catalog || { active: false },
            suggestions: modules.suggestions || { active: false },
          }

          // Dispatch todos los servicios inmediatamente para evitar undefined
          store.dispatch(setServices(criticalServices))
          console.log("📰 All services loaded for newsfeed")
        } else {
          // Si no hay módulos, inicializar con servicios vacíos para evitar undefined
          const emptyServices = {
            recognitions: { active: false },
            communications: { active: false },
            tasks: { active: false },
            programs: { active: false },
            insignias: { active: false },
            workplace: { active: false },
            profile: { active: true }, // Activado para desarrollo
            notifications: { active: false }, // Agregar servicio notifications
            goals: { active: false }, // Agregar servicio goals
            birthday_img: { active: false }, // Agregar servicio birthday_img
            analytics: { active: false },
            benefits: { active: false },
            catalog: { active: false },
            evaluations: { active: false },
            surveys: { active: false },
            events: { active: false },
            benef_corp: { active: false },
            solicitud: { active: false },
            admin_retos: { active: false },
            files_repo: { active: false },
            coverages: { active: false },
            user_event_registration: { active: false },
            ever_health: { active: false },
            salud_seguridad: { active: false },
            invoices: { active: false },
            publicBadges: { active: false },
            maintenance: { active: false },
            complaints_pannel: { active: false },
            complaints: { active: false },
            whatsapp: { active: false },
            motivy_support: { active: false },
            birthdays: { active: false },
            anniversary: { active: false },
            choose_catalog: { active: false },
            suggestions: { active: false },
          }
          store.dispatch(setServices(emptyServices))
          console.log("⚠️ No modules found, initialized with empty services")
        }
      } catch (error) {
        console.warn("Error cargando servicios críticos:", error)
        // Inicializar con servicios vacíos en caso de error
        const errorServices = {
          recognitions: { active: false },
          communications: { active: false },
          tasks: { active: false },
          programs: { active: false },
          insignias: { active: false },
          workplace: { active: false },
          profile: { active: true }, // Activado para desarrollo
          notifications: { active: false }, // Agregar servicio notifications
          goals: { active: false }, // Agregar servicio goals
          birthday_img: { active: false }, // Agregar servicio birthday_img
          analytics: { active: false },
          benefits: { active: false },
          catalog: { active: false },
          evaluations: { active: false },
          surveys: { active: false },
          events: { active: false },
          benef_corp: { active: false },
          solicitud: { active: false },
          admin_retos: { active: false },
          files_repo: { active: false },
          coverages: { active: false },
          user_event_registration: { active: false },
          ever_health: { active: false },
          salud_seguridad: { active: false },
          invoices: { active: false },
          publicBadges: { active: false },
          maintenance: { active: false },
          complaints_pannel: { active: false },
          complaints: { active: false },
          whatsapp: { active: false },
          motivy_support: { active: false },
          birthdays: { active: false },
          anniversary: { active: false },
          choose_catalog: { active: false },
          suggestions: { active: false },
        }
        store.dispatch(setServices(errorServices))
      }
    }

    // Ejecutar inicializaciones críticas primero, luego las no críticas
    const startTime = performance.now()

    // Cargar usuario y servicios críticos para newsfeed
    Promise.allSettled([
      loadUser(), // Usuario crítico
      loadCriticalServices(), // Servicios críticos para newsfeed
    ])
      .then(() => {
        console.log("Critical initialization completed")

        // Usar requestAnimationFrame para mejor rendimiento
        const loadTime = performance.now() - startTime
        const minLoadTime = 600 // Reducido de 800ms a 600ms
        const remainingTime = Math.max(0, minLoadTime - loadTime)

        if (remainingTime > 0) {
          requestAnimationFrame(() => {
            setIsLoading(false)
          })
        } else {
          setIsLoading(false)
        }
      })
      .catch((error) => {
        console.error("Critical error during app initialization:", error)
        setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const mainColor = branding?.banner?.main || undefined
  const secondaryColor = branding?.banner?.secondary || undefined
  const IS_API_DOCS_ENV = window.location.hostname.includes("docs") || false

  return (
    <>
      <ThemeProvider theme={getTheme(mainColor, secondaryColor)}>
        <AlertContext.Provider
          value={useMemo(() => ({ caller: (f: Function) => f() }), [])}
        >
          <ApiContext.Provider
            value={useMemo(
              () => ({
                apiResponses,
                post: { setTeamsSession },
              }),
              [apiResponses, setTeamsSession]
            )}
          >
            {IS_API_DOCS_ENV ? (
              <APIDocs></APIDocs>
            ) : (
              <QueryClientProvider client={queryClient}>
                <ChunkErrorHandler>
                  <QueryErrorBoundary>
                    <ProfileImageCacheProvider>
                      <BrowserRouter
                        future={{
                          v7_startTransition: true,
                          v7_relativeSplatPath: true,
                        }}
                      >
                        <AuthProvider>
                          <>
                            {isLoading && <LoaderPage />}
                            <AppContent isLoading={isLoading} />
                          </>
                        </AuthProvider>
                      </BrowserRouter>
                      {process.env.NODE_ENV === "development" && (
                        <Suspense fallback={null}>
                          <ClickToComponent editor="cursor" />
                          <ReactQueryDevtools
                            initialIsOpen={false}
                            position={"bottom-left"}
                          />
                        </Suspense>
                      )}
                    </ProfileImageCacheProvider>
                  </QueryErrorBoundary>
                </ChunkErrorHandler>
              </QueryClientProvider>
            )}
          </ApiContext.Provider>
        </AlertContext.Provider>
      </ThemeProvider>
    </>
  )
}

export default App
